import useSWR from 'swr';
import { API_GET_LAYOUT, API_GET_WIDGET } from '../api/configurationScreenServices';
import { formatWidgetConfig } from '../utils/globalUtils';
import { deleteWidgetUtil } from '../utils/previewPaneUtils';
import { toastMessageConstants } from '../constants/globalConstant';

const fetchLayout = async (homepageId) => {
  const response = await API_GET_LAYOUT(homepageId);
  const { status, data } = response;

  if (status !== 200) {
    const errorMessage = data?.message || toastMessageConstants.SOMETHING_WENT_WRONG;
    throw new Error(errorMessage);
  }

  return data;
};

const fetchWidgetConfigurations = async (layout, hasDraftChanges) => {
  const { sections } = hasDraftChanges ? layout.draftLayout : layout.mobileLayout;
  let widgetIdList = [];

  sections?.forEach((section) => {
    section.rows.forEach((row) => {
      row.widgets.forEach((widget) => {
        widgetIdList.push(widget.widgetId);
      });
    });
  });

  let widgetTemplateConfigurationList = {};
  let previewPaneData = { sections };

  // Batch fetch widgets (5 at a time)
  while (widgetIdList.length > 0) {
    const widgetBatch = widgetIdList.slice(0, 5);
    widgetIdList.splice(0, 5);

    const widgetPromiseList = widgetBatch.map((widgetId) =>
      API_GET_WIDGET(widgetId).then((response) => response?.data)
    );

    const widgetResponse = await Promise.allSettled(widgetPromiseList);

    widgetResponse.forEach((widget, index) => {
      if (widget.status === 'fulfilled' && widget.value) {
        widgetTemplateConfigurationList[widget.value['widgetId']] = formatWidgetConfig(
          widget?.value,
          hasDraftChanges
        );
      } else {
        // Remove widget if fetch failed
        previewPaneData = deleteWidgetUtil(previewPaneData, widgetBatch[index]);
      }
    });
  }

  return {
    sections: previewPaneData.sections,
    widgetConfiguration: widgetTemplateConfigurationList
  };
};

export function useLayout(homepageId) {
  const { data, error, isLoading, mutate } = useSWR(
    homepageId ? `/homepages/${homepageId}/layout` : null,
    async () => {
      const layout = await fetchLayout(homepageId);
      const { sections, widgetConfiguration } = await fetchWidgetConfigurations(
        layout,
        layout.hasDraftChanges
      );

      return {
        ...layout,
        sections,
        widgetConfiguration
      };
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000
    }
  );

  return {
    layout: data,
    sections: data?.sections || [],
    widgetConfiguration: data?.widgetConfiguration || {},
    hasDraftChanges: data?.hasDraftChanges || false,
    draftLayout: data?.draftLayout,
    publishedLayout: data?.mobileLayout,
    status: data?.status,
    isLoading,
    error,
    mutate
  };
}
